import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Building, Users, UserCheck, ListChecks, CalendarRange, BarChart3, ChevronLeft, ChevronRight, Briefcase, DollarSign, FileText, Mail, Calendar, Palmtree, Cog, BookOpen, Newspaper, Database, Activity, ChevronDown, Clock, MessageCircle, ClipboardList, UserPlus, FolderOpen, Receipt, FileSearch, QrCode, Tags, Target, FileEdit, Book, Coins, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

// Navigation items based on user role and view mode
const getNavItems = (userRole, activeView = null, hasBadges = false, hasMarketingBadge = false, hasCorrespondenceBadge = false, isLineManager = false, isAdmin = false, hasInvoiceBadge = false) => {
  if (userRole === 'super_admin') {
    return [
      { label: 'Overview', icon: LayoutDashboard, path: '/overview' },
      { label: 'My Tasks', icon: ListChecks, path: '/my-tasks' },
      { label: 'Manage Offices', icon: Building, path: '/manage-offices' },
      { label: 'Manage Users', icon: Users, path: '/manage-users' },
      { label: 'Employees', icon: UserCheck, path: '/employees' },
      { label: 'Leads', icon: Target, path: '/leads' },
      { label: 'Meeting Pad', icon: FileEdit, path: '/meeting-pad' },
      {
        label: 'Documents',
        icon: FolderOpen,
        isGroup: true,
        children: [
          { label: 'Documents Ledger', icon: FileText, path: '/documents-ledger' },
          { label: 'Company Credentials', icon: ShieldCheck, path: '/company-credentials' },
          { label: 'Company Policies', icon: BookOpen, path: '/policy-management' },
        ],
      },
      {
        label: 'Finance',
        icon: DollarSign,
        isGroup: true,
        children: [
          { label: 'Invoices', icon: Receipt, path: '/invoices' },
        ],
      },
      { label: 'Documentation', icon: Book, path: '/documentation' },
      { label: 'Platform Analytics', icon: BarChart3, path: '/platform-analytics' },
      { label: 'EcoCoins', icon: Coins, path: '/ecocoins' },
      { label: 'Surveys', icon: ClipboardList, path: '/surveys' },
      { 
        label: 'System Config', 
        icon: Cog, 
        path: '/system-config',
        isGroup: true,
        children: [
          { label: 'General Settings', icon: Cog, path: '/system-config' },
          { label: 'Email Settings', icon: Mail, path: '/admin/email-config' },
          { label: 'Expense Categories', icon: Tags, path: '/admin/expense-categories' },
          { label: 'Expense Thresholds', icon: DollarSign, path: '/admin/expense-settings' },
          { label: 'Timesheet Categories', icon: Tags, path: '/timesheet-categories' },
          { label: 'QR Scan Logs', icon: QrCode, path: '/admin/qr-scan-logs' },
          { label: 'Data Cleanup', icon: Database, path: '/admin/data-management' },
          { label: 'Audit Trail', icon: Activity, path: '/audit-trail' },
        ]
      },
    ];
  } else if (userRole === 'office_admin') {
    // Office admin viewing as employee shows employee menu
    if (activeView === 'employee') {
      const items = [
        { label: 'Overview', icon: LayoutDashboard, path: '/overview' },
        { label: 'My Profile', icon: UserCheck, path: '/profile' },
        { label: 'Attendance', icon: Clock, path: '/attendance' },
        { label: 'My Tasks', icon: ListChecks, path: '/my-tasks' },
        { label: 'My Calendar', icon: Calendar, path: '/my-calendar' },
      ];
      
      // Finance group
      const financeChildren = [
        { label: 'My Expenses', icon: Receipt, path: '/my-expenses' },
      ];
      if (hasBadges) {
        financeChildren.push({ label: 'Expense Approvals', icon: FileSearch, path: '/expense-management' });
      }
      financeChildren.push({ label: 'My Salary', icon: DollarSign, path: '/my-salary' });
      financeChildren.push({ label: 'My Coins', icon: Coins, path: '/my-coins' });
      if (hasInvoiceBadge) {
        financeChildren.push({ label: 'Invoices', icon: Receipt, path: '/invoices' });
      }
      items.push({
        label: 'Finance',
        icon: DollarSign,
        isGroup: true,
        children: financeChildren,
      });
      
      // Add Leads if user has Marketing Manager badge
      if (hasMarketingBadge) {
        items.push({ label: 'Leads', icon: Target, path: '/leads' });
      }
      
      // Add Letters if user has Correspondence Manager badge
      if (hasCorrespondenceBadge) {
        items.push({ label: 'Letters', icon: Mail, path: '/letters' });
      }
      
      // Meeting Pad is accessible to all users
      items.push({ label: 'Meeting Pad', icon: FileEdit, path: '/meeting-pad' });

      // Office admin viewing as employee — they often act as line manager too
      if (isLineManager) {
        items.push({ label: 'Team Leave', icon: Palmtree, path: '/team-leave' });
      }
      
      items.push(
        { label: 'My Team', icon: Users, path: '/team-calendar' },
        { label: 'My Leave', icon: Palmtree, path: '/my-leave' },
        { label: 'Newsroom', icon: Newspaper, path: '/newsroom' },
        { label: 'Company Policies', icon: BookOpen, path: '/company-policies' },
      );
       
      return items;
    }
    
    // Default admin view - always show Leads for Office Admin
    return [
      { label: 'Overview', icon: LayoutDashboard, path: '/overview' },
      { 
        label: 'My Work', 
        icon: ClipboardList, 
        isGroup: true,
        children: [
          { label: 'My Tasks', icon: ListChecks, path: '/my-tasks' },
          { label: 'My Calendar', icon: Calendar, path: '/my-calendar' },
          { label: 'My Expenses', icon: Receipt, path: '/my-expenses' },
          { label: 'My Timesheet', icon: Clock, path: '/my-timesheet' },
        ]
      },
      { 
        label: 'Team', 
        icon: Users, 
        isGroup: true,
        children: [
          { label: 'Employees', icon: UserCheck, path: '/employees' },
          { label: 'My Team', icon: Users, path: '/team-calendar' },
          { label: 'Leads', icon: Target, path: '/leads' },
        ]
      },
      { 
        label: 'HR Operations', 
        icon: UserPlus, 
        isGroup: true,
        children: [
          { label: 'Recruitment', icon: Briefcase, path: '/recruitment' },
          { label: 'Onboarding', icon: ListChecks, path: '/onboarding' },
          { label: 'Attendance', icon: Clock, path: '/attendance' },
          { label: 'Timesheets', icon: FileEdit, path: '/timesheet-review' },
          { label: 'Leave', icon: Palmtree, path: '/leave-management' },
          { label: 'Performance', icon: BarChart3, path: '/performance' },
          { label: 'Office Analytics', icon: Activity, path: '/platform-analytics' },
        ]
      },
      { 
        label: 'Finance', 
        icon: DollarSign, 
        isGroup: true,
        children: [
          ...(hasBadges || isAdmin ? [{ label: 'Expenses', icon: Receipt, path: '/expense-management' }] : []),
          { label: 'Payroll', icon: DollarSign, path: '/payroll' },
          { label: 'My Coins', icon: Coins, path: '/my-coins' },
          { label: 'Invoices', icon: Receipt, path: '/invoices' },
        ]
      },
      { 
        label: 'Communications', 
        icon: FolderOpen, 
        isGroup: true,
        children: [
          { label: 'Letters', icon: Mail, path: '/letters' },
          { label: 'Newsroom', icon: Newspaper, path: '/news-management' },
          { label: 'Company Policies', icon: BookOpen, path: '/company-policies' },
          { label: 'Contracts', icon: FileText, path: '/contracts' },
          { label: 'Company Credentials', icon: ShieldCheck, path: '/company-credentials' },
        ]
      },
    ];
  } else {
    // Employee view - Ping is accessed via header icon, not sidebar
    const items = [
      { label: 'Overview', icon: LayoutDashboard, path: '/overview' },
      { label: 'My Profile', icon: UserCheck, path: '/profile' },
      { label: 'Attendance', icon: Clock, path: '/attendance' },
      { label: 'My Tasks', icon: ListChecks, path: '/my-tasks' },
      { label: 'My Calendar', icon: Calendar, path: '/my-calendar' },
      { label: 'My Timesheet', icon: Clock, path: '/my-timesheet' },
    ];
    
    // Finance group
    const financeChildren = [
      { label: 'My Expenses', icon: Receipt, path: '/my-expenses' },
    ];
    if (hasBadges) {
      financeChildren.push({ label: 'Expense Approvals', icon: FileSearch, path: '/expense-management' });
    }
    financeChildren.push({ label: 'My Salary', icon: DollarSign, path: '/my-salary' });
    financeChildren.push({ label: 'My Coins', icon: Coins, path: '/my-coins' });
    if (hasInvoiceBadge) {
      financeChildren.push({ label: 'Invoices', icon: Receipt, path: '/invoices' });
    }
    items.push({
      label: 'Finance',
      icon: DollarSign,
      isGroup: true,
      children: financeChildren,
    });
    
    // Add Timesheets review for line managers
    if (isLineManager) {
      items.push({ label: 'Timesheets', icon: FileEdit, path: '/timesheet-review' });
      items.push({ label: 'Team Leave', icon: Palmtree, path: '/team-leave' });
    }
    
    // Add Leads if user has Marketing Manager badge
    if (hasMarketingBadge) {
      items.push({ label: 'Leads', icon: Target, path: '/leads' });
    }
    
    // Add Letters if user has Correspondence Manager badge
    if (hasCorrespondenceBadge) {
      items.push({ label: 'Letters', icon: Mail, path: '/letters' });
    }
    
    // Meeting Pad is accessible to all employees
    items.push({ label: 'Meeting Pad', icon: FileEdit, path: '/meeting-pad' });
    
    if (isLineManager) {
      items.push({ label: 'My Team', icon: CalendarRange, path: '/team-calendar' });
    }
    items.push(
      { label: 'My Leave', icon: Palmtree, path: '/my-leave' },
      { label: 'Newsroom', icon: Newspaper, path: '/newsroom' },
      { label: 'Company Policies', icon: BookOpen, path: '/company-policies' },
    );
    
    return items;
  }
};

const Sidebar = ({ collapsed, onToggle }) => {
  // Read from AuthContext so we react to view switches and office changes
  // (previously we read localStorage synchronously, which meant the sidebar
  // would not re-check the "is line manager" state when an office admin
  // toggled into Employee view — Team Leave silently stayed hidden).
  const { user: ctxUser, activeView: ctxActiveView, currentOffice } = useAuth();
  const savedUser = localStorage.getItem('auth_user');
  const currentUser = ctxUser || (savedUser ? JSON.parse(savedUser) : null);
  const userRole = currentUser?.roles?.[0]?.role;
  const activeView = userRole === 'office_admin'
    ? (ctxActiveView || localStorage.getItem('active_view'))
    : null;
  
  const [expandedGroups, setExpandedGroups] = useState({});
  const [hasBadges, setHasBadges] = useState(false);
  const [isLineManager, setIsLineManager] = useState(false);
  const [isExpenseAdmin, setIsExpenseAdmin] = useState(false);
  
  // Check for Marketing Manager badge directly from user object
  const hasMarketingBadge = currentUser?.special_badges?.some(b => 
    b.badge_type === 'marketing_manager'
  ) || false;
  
  // Check for Correspondence Manager badge
  const hasCorrespondenceBadge = currentUser?.special_badges?.some(b => 
    b.badge_type === 'correspondence_manager'
  ) || false;

  // Check for Invoice Manager badge
  const hasInvoiceBadge = currentUser?.special_badges?.some(b => 
    b.badge_type === 'invoice_manager'
  ) || false;

  // Check if user has workflow badges (for expense approvals menu) and is a
  // line manager. Re-runs when the user, current office, or active view
  // changes — that way toggling into Employee view immediately refreshes
  // the line-manager flag and surfaces the Team Leave menu.
  useEffect(() => {
    const officeId = currentOffice?.id
      || (localStorage.getItem('current_office')
        ? JSON.parse(localStorage.getItem('current_office'))?.id
        : null);
    const token = localStorage.getItem('auth_token');

    if (!token || !currentUser?.id || !officeId) return;

    const checkBadges = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;

        // Check for workflow badges (expense approvals)
        const response = await fetch(`${API_URL}/api/expenses/workflow/my-queue`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Office-ID': officeId,
          }
        });

        if (response.ok) {
          const data = await response.json();
          const approvalBadges = ['approver', 'reviewer', 'authorizer'];
          const hasApprovalBadge = data.badges && data.badges.some(b => approvalBadges.includes(b.toLowerCase()));
          setHasBadges(hasApprovalBadge);
          setIsExpenseAdmin(data.is_admin === true);
        }

        // Check if user is a line manager (has direct reports)
        const teamRes = await fetch(`${API_URL}/api/employees/my-team`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Office-ID': officeId,
          }
        });
        if (teamRes.ok) {
          const teamData = await teamRes.json();
          setIsLineManager(Array.isArray(teamData) && teamData.length > 0);
        }
      } catch (error) {
        console.error('Error checking badges:', error);
      }
    };

    checkBadges();
  }, [currentUser?.id, currentOffice?.id, activeView]);

  const navItems = getNavItems(userRole, activeView, hasBadges, hasMarketingBadge, hasCorrespondenceBadge, isLineManager, isExpenseAdmin, hasInvoiceBadge);

  const toggleGroup = (label) => {
    setExpandedGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <motion.aside
      initial={{ width: collapsed ? 80 : 288 }}
      animate={{ width: collapsed ? 80 : 288 }}
      transition={{ duration: 0.22, ease: [0.2, 0.0, 0, 1] }}
      className="bg-card border-r border-border flex flex-col h-full"
      data-testid="sidebar"
    >
      {/* Header */}
      <div className="p-6 border-b border-border flex items-center justify-between">
        {!collapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_9304be5e-5dfb-4406-9a75-7c4b3ad72b13/artifacts/f33mtxoy_ECOWATER_WORDMARK_LOGO.png"
              alt="Ecowater Workspace"
              className="h-6"
            />
          </motion.div>
        )}
        {collapsed && (
          <div className="w-8 h-8 rounded-lg flex items-center justify-center mx-auto">
            <img
              src="https://customer-assets.emergentagent.com/job_9304be5e-5dfb-4406-9a75-7c4b3ad72b13/artifacts/f33mtxoy_ECOWATER_WORDMARK_LOGO.png"
              alt="EW"
              className="h-5 object-contain"
            />
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {navItems.map((item, idx) => (
          item.isGroup ? (
            <div key={item.label} className="space-y-1">
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(item.label)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:text-foreground hover:bg-accent`}
                data-testid={`nav-group-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">{item.label}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${expandedGroups[item.label] ? 'rotate-180' : ''}`} />
                  </>
                )}
              </button>
              
              {/* Group Children */}
              <AnimatePresence>
                {(expandedGroups[item.label] || collapsed) && !collapsed && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="ml-4 space-y-1 overflow-hidden"
                  >
                    {item.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                            isActive
                              ? 'text-white shadow-sm'
                              : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                          }`
                        }
                        style={({ isActive }) => isActive ? { backgroundColor: '#0779BC' } : {}}
                        data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <child.icon className="w-4 h-4 flex-shrink-0" />
                        <span>{child.label}</span>
                      </NavLink>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? 'text-white shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`
              }
              style={({ isActive }) => isActive ? { backgroundColor: '#0779BC' } : {}}
              data-testid={item.testId || `nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 + idx * 0.02 }}
                >
                  {item.label}
                </motion.span>
              )}
            </NavLink>
          )
        ))}
      </nav>

      {/* Toggle Button */}
      <div className="p-3 border-t border-border">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-accent transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5 text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Collapse</span>
            </>
          )}
        </button>
      </div>
    </motion.aside>
  );
};

export default Sidebar;
